import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { AxiosResponse } from 'axios';
import { ReportData } from '../../model/reportData';
import {
  getIncompleteEvents,
  getNotUploadedEvents,
  getPictureDayConnectionDetails,
  getQuarantineEvents,
  getTodayEmCompleteDetails
} from '../../api/ReportsApi';
import { ApiStatus } from '../../api/apiStatus';
import { formattedZoneTime } from '../helper/dateFormatHelper';

export interface ReportState {
  events: Array<ReportData>;
  pictureDays: Array<ReportData>;
  notUploadedEvents: Array<ReportData>;
  quarantineEvents: Array<ReportData>;
  todayEventComplete: Array<ReportData>;
  pictureDayReportStatus: ApiStatus;
  pendingEventsStatus: ApiStatus;
  notUploadedEventsStatus: ApiStatus;
  quarantineEventsStatus: ApiStatus;
  todayEventCompleteStatus: ApiStatus;
}

const initialState: ReportState = {
  events: [],
  pictureDays: [],
  notUploadedEvents: [],
  quarantineEvents: [],
  pendingEventsStatus: null,
  notUploadedEventsStatus: null,
  pictureDayReportStatus: null,
  quarantineEventsStatus: null,
  todayEventCompleteStatus: null,
  todayEventComplete: [],
};

// get incomplete events
export const pendingEvents = createAsyncThunk(
  'events/incompleteEvents',
  async (payload: any, { rejectWithValue }) => {
    let areas: string[] = payload.areas;
    let queryString: string = '?lastSixMonths=' + !!payload.lastSixMonths;
    try {
      if (areas.length > 0) {
        areas.forEach((area) => {
          if (queryString.length === 0) {
            queryString += '?areas=' + area;
          } else {
            queryString += '&areas=' + area;
          }
        });
      }
      return await getIncompleteEvents(queryString);
    } catch (err: any) {
      return rejectWithValue((err.response as AxiosResponse).data?.message || err.message);
    }
  }
);

//get picture day connection details
export const loadPictureDayConnectionDetails = createAsyncThunk(
  'events/todayUploadDetails',
  async (area: string, { rejectWithValue }) => {
    try {
      if (!!area) {
        const queryString = '?territory=' + area;
        return await getPictureDayConnectionDetails(queryString);
      }
    } catch (err: any) {
      return rejectWithValue((err.response as AxiosResponse).data?.message || err.message);
    }
  }
);

//get today EM complete details
export const loadTodayEmCompleteDetails = createAsyncThunk(
  'events/todayEmCompleteDetails',
  async (area: string, { rejectWithValue }) => {
    try {
      if (!!area) {
        const queryString = '?territory=' + area;
        return await getTodayEmCompleteDetails(queryString);
      }
    } catch (err: any) {
      return rejectWithValue((err.response as AxiosResponse).data?.message || err.message);
    }
  }
);

// get not uploaded events
export const loadNotUploadedEvents = createAsyncThunk(
  'events/notUploadedEvents',
  async (payload: any, { rejectWithValue }) => {
    let areas: string[] = payload.areas;
    let queryString: string = '?lastSixMonths=' + payload.lastSixMonths;
    try {
      if (areas.length > 0) {
        areas.forEach((area) => {
          if (queryString.length === 0) {
            queryString += '?areas=' + area;
          } else {
            queryString += '&areas=' + area;
          }
        });
      }
      return await getNotUploadedEvents(queryString);
    } catch (err: any) {
      return rejectWithValue((err.response as AxiosResponse).data?.message || err.message);
    }
  }
);

// get quarantine events
export const loadQuarantineEvents = createAsyncThunk(
  'events/quarantineEvents',
  async (payload: any, { rejectWithValue }) => {
    let areas: string[] = payload.areas;
    let queryString: string = '?resolved=' + payload.showResolved;
    try {
      if (areas.length > 0) {
        areas.forEach((area) => {
          if (queryString.length === 0) {
            queryString += '?areas=' + area;
          } else {
            queryString += '&areas=' + area;
          }
        });
        return await getQuarantineEvents(queryString);
      }
    } catch (err: any) {
      return rejectWithValue((err.response as AxiosResponse).data?.message || err.message);
    }
  }
);

export const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(pendingEvents.fulfilled, (state, action) => {
        state.events = action.payload || [];
        state.pendingEventsStatus = 'SUCCESS';
      })
      .addCase(pendingEvents.pending, (state) => {
        state.pendingEventsStatus = 'LOADING';
      })
      .addCase(pendingEvents.rejected, (state, action) => {
        state.pendingEventsStatus = 'FAILED';
        state.events = [];
      })
      .addCase(loadPictureDayConnectionDetails.fulfilled, (state, action) => {
        state.pictureDays = action.payload || [];
        state.pictureDayReportStatus = 'SUCCESS';
      })
      .addCase(loadPictureDayConnectionDetails.pending, (state) => {
        state.pictureDayReportStatus = 'LOADING';
      })
      .addCase(loadPictureDayConnectionDetails.rejected, (state, action) => {
        state.pictureDayReportStatus = 'FAILED';
        state.pictureDays = [];
      })
      .addCase(loadNotUploadedEvents.fulfilled, (state, action) => {
        state.notUploadedEvents = action.payload || [];
        state.notUploadedEventsStatus = 'SUCCESS';
      })
      .addCase(loadNotUploadedEvents.pending, (state) => {
        state.notUploadedEventsStatus = 'LOADING';
      })
      .addCase(loadNotUploadedEvents.rejected, (state, action) => {
        state.notUploadedEventsStatus = 'FAILED';
        state.notUploadedEvents = [];
      })
      .addCase(loadQuarantineEvents.fulfilled, (state, action) => {
        const rawEvents: any[] = (action.payload as any[]) || [];
        state.quarantineEvents = rawEvents.map((event: any) => {
          const pibDetails = (event.pibDetails || []).map((pib: any) => ({
            photographerId: pib.photographerId ?? '',
            cameraSerial: pib.cameraId ?? '',
            eventScheduledDate: event.eventScheduledDate ?? '',
            numberOfOrders: pib.orderCount ?? 0,
          }));
          const firstPib = event.pibDetails?.[0];
          return {
            ...event,
            photographerId: firstPib?.photographerId ?? '',
            cameraSerialNumber: firstPib?.cameraId ?? '',
            numberOfOrders: firstPib?.orderCount ?? 0,
            pibDetails: pibDetails.length > 0 ? pibDetails : undefined,
          };
        });
        state.quarantineEventsStatus = 'SUCCESS';
      })
      .addCase(loadQuarantineEvents.pending, (state) => {
        state.quarantineEventsStatus = 'LOADING';
      })
      .addCase(loadQuarantineEvents.rejected, (state, action) => {
        state.quarantineEventsStatus = 'FAILED';
        state.quarantineEvents = [];
      })
      .addCase(loadTodayEmCompleteDetails.fulfilled, (state, action) => {
        let emCompleteList = []
        let emCompPayload: ReportData[] = action.payload ||[]
        if (!!emCompPayload) {
          for(let emComp of emCompPayload) {
           const time = formattedZoneTime(emComp.emCompleteDate, true)
            if (!!time) {
              emComp.emCompleteDate = time
            }
            emCompleteList.push(emComp)
          }
        }

        state.todayEventComplete = emCompleteList || [];
        state.todayEventCompleteStatus = 'SUCCESS';
      })
      .addCase(loadTodayEmCompleteDetails.pending, (state) => {
        state.todayEventCompleteStatus = 'LOADING';
      })
      .addCase(loadTodayEmCompleteDetails.rejected, (state, action) => {
        state.todayEventCompleteStatus = 'FAILED';
        state.todayEventComplete = [];
      });
  }
});

// selectors
export const selectPendingEvents = (state: RootState) => state.report.events;
export const selectPendingEventsStatus = (state: RootState) => state.report.pendingEventsStatus;

export const selectPictureDayConnectionDetails = (state: RootState) => state.report.pictureDays;
export const selectPictureDayConnectionDetailsStatus = (state: RootState) => state.report.pictureDayReportStatus;

export const selectNotUploadedEvents = (state: RootState) => state.report.notUploadedEvents;
export const selectNotUploadedEventsStatus = (state: RootState) => state.report.notUploadedEventsStatus;

export const selectQuarantineEvents = (state: RootState) => state.report.quarantineEvents;
export const selectQuarantineEventsStatus = (state: RootState) => state.report.quarantineEventsStatus;
export const selectTodayEventComplete = (state: RootState) => state.report.todayEventComplete;
export const selectTodayEventCompleteStatus = (state: RootState) => state.report.todayEventCompleteStatus;
export default reportSlice.reducer;
